import React, { useEffect, useState, useCallback, useRef } from "react";
import { Dropdown, Modal, Button, Nav, OverlayTrigger, Tooltip } from "react-bootstrap";
import { Formik, Field, Form as FormikForm, ErrorMessage } from "formik";
import * as Yup from "yup";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";
import Spinner from "../components/Common/Spinner";
import { useLocation } from "react-router-dom";
import {
  addFile,
  createNewAdmin,
  deleteAdmin,
  getUrl,
  updateAdminInfo,
  updateAdminStatus,
} from "../../services/Admin/adminServices";
import Pagination from "../components/Pagination";
import { useNavigate } from "react-router-dom";
import "react-phone-input-2/lib/style.css";
import { getUserList, getFaceVerifyPendingUser, verifyUser, exportUsersListData } from "../../services/UserServices/UserService";
import LocationSearchDropdown from "../components/LocationAdd";

const ReadMoreText = ({ text, maxLength = 30, maxWidth = "220px" }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!text) return "_";

  const textStr = typeof text === "string" ? text : String(text);
  const formattedText = textStr.charAt(0).toUpperCase() + textStr.slice(1);

  if (formattedText.length <= maxLength) {
    return <span style={{ whiteSpace: "normal", wordBreak: "break-word" }}>{formattedText}</span>;
  }

  return (
    <div style={{ maxWidth: isExpanded ? "350px" : maxWidth, wordBreak: "break-word", whiteSpace: "normal" }}>
      {isExpanded ? formattedText : `${formattedText.slice(0, maxLength)}...`}
      <button
        type="button"
        className="btn btn-link p-0 text-primary fw-semibold ms-1"
        style={{ fontSize: "12px", textDecoration: "none", boxShadow: "none", verticalAlign: "baseline" }}
        onClick={(e) => {
          e.stopPropagation();
          setIsExpanded(!isExpanded);
        }}
      >
        {isExpanded ? "Read Less" : "Read More"}
      </button>
    </div>
  );
};

const RequestPage = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const location = useLocation();
  const [locationText, setLocationText] = useState(
    location.state?.locationText || ""
  );
  const [coordinates, setCoordinates] = useState(
    location.state?.coordinates || { lat: "", long: "" }
  );
  const [search, setSearch] = useState(
    location.state?.search || ""
  );
  const [debouncedSearch, setDebouncedSearch] = useState(
    location.state?.search || ""
  );
  const [startDate, setStartDate] = useState(
    location.state?.startDate || ""
  );
  const [endDate, setEndDate] = useState(
    location.state?.endDate || ""
  );
  const [currentPage, setCurrentPage] = useState(
    location.state?.currentPage || 0
  );
  const [itemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [activeTab, setActiveTab] = useState(
    location.state?.activeTab || "Pending"
  );
  const [type, setType] = useState(
    location.state?.type || "Pending"
  );

  const [confirmState, setConfirmState] = useState({
    show: false,
    action: null,
    id: null,
    status: null,
    message: "",
  });
  const navigate = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState("");
  const fileInputRef = useRef(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [submit, setSubmit] = useState(false);

  const [showExportModal, setShowExportModal] = useState(false);
  const [exportType, setExportType] = useState("excel");
  const [exportStartDate, setExportStartDate] = useState("");
  const [exportEndDate, setExportEndDate] = useState("");
  const [exportLocationText, setExportLocationText] = useState("");
  const [exportCoordinates, setExportCoordinates] = useState({ lat: "", long: "" });
  const [exportStatus, setExportStatus] = useState("Pending");
  const [exportLoading, setExportLoading] = useState(false);

  const svg1 = (
    <svg width="20px" height="24px" viewBox="0 0 24 24" version="1.1">
      <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
        <rect x="0" y="0" width="24" height="24"></rect>
        <circle fill="#000000" cx="12" cy="5" r="2"></circle>
        <circle fill="#000000" cx="12" cy="12" r="2"></circle>
        <circle fill="#000000" cx="12" cy="19" r="2"></circle>
      </g>
    </svg>
  );

  const fetchAdmins = useCallback(async () => {
    try {
      setLoading(true);
      let response;
      if (type === "Unverified Faces") {
        response = await getFaceVerifyPendingUser(
          currentPage,
          itemsPerPage,
          startDate,
          endDate,
          debouncedSearch,
          coordinates.lat,
          coordinates.long
        );
      } else {
        const filterType = (type === "Pending" || type === "unVerified") ? type : "Pending";
        response = await getUserList(
          currentPage,
          itemsPerPage,
          startDate,
          endDate,
          filterType,
          debouncedSearch,
          coordinates.lat,
          coordinates.long
        );
      }
      setAdmins(response?.data?.data?.data || []);
      setTotalItems(response?.data?.data?.total || 0);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, debouncedSearch, startDate, endDate, type, coordinates]);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [search]);
  useEffect(() => {
    if (currentPage !== undefined) {
      navigate(".", {
        replace: true,
        state: { ...location.state, currentPage, activeTab, search, startDate, endDate, type, locationText, coordinates },
      });
    }
  }, [currentPage, activeTab, search, startDate, endDate, type, locationText, coordinates]);

  const isComingFromDetail = useRef(!!location.state);
  useEffect(() => {
    if (isComingFromDetail.current) {
      isComingFromDetail.current = false; // skip only first time
      return;
    }
    setCurrentPage(0);
  }, [debouncedSearch, startDate, endDate, type, activeTab, coordinates]);

  const firstRender = useRef(true);

  // useEffect(() => {
  //   if (firstRender.current) {
  //     firstRender.current = false;
  //     return;
  //   }

  //   setSearch("");
  //   setCurrentPage(0);
  //   setStartDate("");
  //   setEndDate("");
  //   setType("");
  // }, [activeTab]);
  const getUrlImage = async (img) => {
    try {
      return await getUrl(img);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteAdmin(id);
      toast.success("Successfully deleted");
      fetchAdmins();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to delete user");
      console.error(error);
    }
  };



  const handlestatus = async (id) => {
    try {
      const payload = { userId: id };
      await updateAdminStatus(payload);
      toast.success("Status updated successfully");
      fetchAdmins();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update status");
      console.error(error);
    }
  };

  const handleFileUpload = async (e, fieldName, setFieldValue) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", file);
      const res = await addFile(formData);

      if (res) {
        setPreview(URL.createObjectURL(file));
        setFieldValue("image", res?.data?.data?.fileUrl);
      }
    } catch (error) {
      toast.error("File upload failed");
      setPreview("");
    } finally {
      setUploading(false);
    }
  };

  const openModal = async (admin = null) => {
    setSelectedAdmin(admin);
    setShowModal(true);

    if (admin?.image) {
      try {
        setImageLoading(true);
        const res = await getUrlImage(admin.image);
        setPreview(res?.data?.data);
      } catch (error) {
        console.error(error);
      } finally {
        setImageLoading(false);
      }
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedAdmin(null);
    setPreview("");

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const AdminSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    image: Yup.string().required("Image is required"),
  });

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handleSubmit = async (values, { resetForm }) => {
    try {
      setSubmit(true);
      if (selectedAdmin) {
        const payload = {
          id: selectedAdmin._id,
          name: values.name,
          image: values.image,
        };
        await updateAdminInfo(payload);
        toast.success("Updated successfully");
      } else {
        await createNewAdmin(values);
        toast.success("Added successfully");
        resetForm();
      }
      fetchAdmins();
      setShowModal(false);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to save admin");
    } finally {
      setSubmit(false);
    }
  };

  const isFilterActive = search || startDate || endDate || (type && type !== "Pending") || locationText;

  const handleRequest = async (status, id) => {
    try {
      const payload = {
        userId: id,
        status: status,
      };
      await verifyUser(payload);
      toast.success(`Request successfully ${status === "accept" ? "approved" : "processed"}`);
      fetchAdmins();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || "Failed to process request");
    }
  };

  const handleClear = () => {
    setSearch("");
    setStartDate("");
    setEndDate("");
    setType("Pending");
    setLocationText("");
    setCoordinates({ lat: "", long: "" });
  };

  const triggerConfirm = (action, id, status = null) => {
    let message = "";
    if (action === "delete") {
      message = "Are you sure you want to delete this record? This action cannot be undone.";
    } else if (action === "status") {
      message = `Are you sure you want to ${status.toLowerCase()} this account?`;
    } else if (action === "accept" || action === "Verified" || action === "Approve") {
      message = `Are you sure you want to approve this user?`;
    } else if (action === "reject" || action === "Reject") {
      message = `Are you sure you want to reject this user's request?`;
    }

    setConfirmState({ show: true, action, id, status, message });
  };

  const confirmActionHandler = () => {
    const { action, id, status } = confirmState;
    if (action === "delete") {
      handleDelete(id);
    } else if (action === "status") {
      handlestatus(id);
    } else if (action === "accept" || action === "Verified" || action === "Approve") {
      handleRequest("accept", id);
    } else if (action === "reject" || action === "Reject") {
      handleRequest("reject", id);
    }

    setConfirmState({
      show: false,
      action: null,
      id: null,
      status: null,
      message: "",
    });
  };
  const today = new Date().toISOString().split("T")[0];
  const openExportModal = () => {
    setExportStartDate(startDate || "");
    setExportEndDate(endDate || "");
    setExportLocationText(locationText || "");
    setExportCoordinates(coordinates || { lat: "", long: "" });
    setExportStatus(type || "Pending");
    setShowExportModal(true);
  };

  const handleExportSubmit = async (e) => {
    e.preventDefault();

    if (!exportStartDate || !exportEndDate) {
      toast.error("Start Date and End Date are compulsory for export");
      return;
    }

    if (exportStartDate > exportEndDate) {
      toast.error("Start Date cannot be after End Date");
      return;
    }

    try {
      setExportLoading(true);
      const res = await exportUsersListData(
        exportStartDate,
        exportEndDate,
        exportCoordinates.lat,
        exportCoordinates.long,
        "Pending",
        exportStatus
      );

      const rawData = res?.data?.data;
      let usersList = [];
      if (Array.isArray(rawData)) {
        usersList = rawData;
      } else if (rawData?.users && Array.isArray(rawData.users)) {
        usersList = rawData.users;
      } else if (rawData?.data && Array.isArray(rawData.data)) {
        usersList = rawData.data;
      } else if (rawData?.userList && Array.isArray(rawData.userList)) {
        usersList = rawData.userList;
      }

      if (!usersList || usersList.length === 0) {
        toast.error("No data found for the selected date range and filters");
        return;
      }

      const csvData = usersList.map((user, index) => {
        let userPrefs = "";
        if (Array.isArray(user.userPreference) && user.userPreference.length > 0) {
          userPrefs = user.userPreference
            .map((p) => `${p.question || ""}: ${p.answer || ""}`)
            .filter((str) => str !== ": ")
            .join("; ");
        }

        return {
          "Sr No": index + 1,
          "User ID": user._id || "",
          Name: user.name || "",
          Email: user.email || "",
          Phone: user.phoneNumber
            ? `${user.countryCode || ""} ${user.phoneNumber}`.trim()
            : "",
          Gender: user.gender || "",
          "Date of Birth": user.dateOfBirth
            ? new Date(user.dateOfBirth).toLocaleDateString("en-CA")
            : "",
          Address: user.address || "",
          "Current City": user.basicInfo?.currentCity || "",
          Occupation: user.basicInfo?.occupation || user.occupation || "",
          Organization: user.basicInfo?.organization || "",
          Education: user.basicInfo?.education || "",
          Institution: user.basicInfo?.institution || "",
          Language: user.language || "",
          About: user.about || "",
          "Admin Approval": user.adminApprove || "",
          Status: user.isBlocked ? "Disabled" : "Enabled",
          "Email Verified": user.isVerifyMail ? "Yes" : "No",
          "Phone Verified": user.isVerifyPhoneNumber ? "Yes" : "No",
          "Aadhar Verified": user.isAadharVerify ? "Yes" : "No",
          "Face Verified": user.isFaceVerify ? "Yes" : "No",
          "Basic Info": user.isBasicInfo ? "Yes" : "No",
          "User Preference": user.isUserPreference ? "Yes" : "No",
          "Terms & Conditions": user.isTermsAndConditions ? "Yes" : "No",
          "Looking Ad Type": user.lookingAdType || "",
          Rating: user.rating ?? "",
          "Ads Limit": user.adsLimit ?? "",
          "Total Safety Checks": user.totalSafetyChecks ?? "",
          "User Preferences": userPrefs,
          "Last Seen": user.lastSeen
            ? `${new Date(user.lastSeen).toLocaleDateString("en-CA")} ${new Date(
              user.lastSeen
            ).toLocaleTimeString("en-CA", { hour: "2-digit", minute: "2-digit" })}`
            : "",
          "Created At": user.createdAt
            ? `${new Date(user.createdAt).toLocaleDateString("en-CA")} ${new Date(
              user.createdAt
            ).toLocaleTimeString("en-CA", { hour: "2-digit", minute: "2-digit" })}`
            : "",
        };
      });

      if (exportType === "excel") {
        const worksheet = XLSX.utils.json_to_sheet(csvData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Pending Users");
        XLSX.writeFile(
          workbook,
          `pending_users_export_${exportStartDate}_to_${exportEndDate}.xlsx`
        );
        toast.success("Excel exported successfully");
      } else {
        const headers = Object.keys(csvData[0]);
        const csvContent = [
          headers.join(","),
          ...csvData.map((row) =>
            headers
              .map((header) => `"${String(row[header] ?? "").replace(/"/g, '""')}"`)
              .join(",")
          ),
        ].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.href = url;
        link.setAttribute(
          "download",
          `pending_users_export_${exportStartDate}_to_${exportEndDate}.csv`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        toast.success("CSV exported successfully");
      }
      setShowExportModal(false);
    } catch (error) {
      console.error("Export Error:", error);
      toast.error(error?.response?.data?.message || "Failed to export data");
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="container mt-4">
      {/* Upper header section */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h1 style={{ color: "#E5484D", fontWeight: "600" }}>New Requests</h1>
        {/* <button
          className="btn text-white px-3 py-2"
          style={{ backgroundColor: "#E5484D", borderRadius: "0.5rem" }}
          onClick={openExportModal}
        >
          Export Users
        </button> */}
      </div>



      <div className="pb-2 d-flex flex-wrap justify-content-between gap-2 align-items-end mb-4">
        <div className="flex-grow-1" style={{ minWidth: "200px" }}>
          <label className="form-label mb-1" style={{ fontSize: "12px", color: "#6c757d" }}>
            Location
          </label>
          <LocationSearchDropdown
            value={locationText}
            onChange={setLocationText}
            onSelect={(locationData) => {
              setLocationText(locationData.name);
              const lat = locationData.location?.coordinates?.[1];
              const long = locationData.location?.coordinates?.[0];
              setCoordinates({ lat, long });
            }}
          />
        </div>

        <div className="flex-grow-1" style={{ minWidth: "200px" }}>
          <label className="form-label mb-1" style={{ fontSize: "12px", color: "#6c757d" }}>
            Search
          </label>
          <input
            type="text"
            style={{ borderRadius: "0.5rem", borderColor: "#E5484D", height: "42px" }}
            className="form-control w-100"
            placeholder="Search by name, email....."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div>
          <label className="form-label mb-1" style={{ fontSize: "12px", color: "#6c757d" }}>
            Start Date
          </label>
          <input
            type="date"
            style={{ borderRadius: "0.5rem", borderColor: "#E5484D", height: "42px" }}
            className="form-control"
            value={startDate}
            max={today}
            onChange={(e) => {
              setStartDate(e.target.value);
              if (endDate && e.target.value > endDate) {
                setEndDate("");
              }
            }}
          />
        </div>

        <div>
          <label className="form-label mb-1" style={{ fontSize: "12px", color: "#6c757d" }}>
            End Date
          </label>
          <input
            type="date"
            style={{ borderRadius: "0.5rem", borderColor: "#E5484D", height: "42px" }}
            className="form-control"
            value={endDate}
            min={startDate}
            disabled={!startDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        <div>
          <label className="form-label mb-1" style={{ fontSize: "12px", color: "#6c757d" }}>
            Status
          </label>
          <select
            className="form-control"
            value={type}
            style={{ borderRadius: "0.5rem", borderColor: "#E5484D", height: "42px" }}
            onChange={(e) => setType(e.target.value)}
          >
            <option value="Pending">OTP Verified</option>
            <option value="Unverified Faces">Face Verification Failed</option>
            <option value="unVerified">Aadhaar Verified</option>
          </select>
        </div>

        {isFilterActive && (
          <div>
            <button
              className="btn btn-outline-danger"
              onClick={handleClear}
              style={{ borderRadius: "0.5rem", height: "42px" }}
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Filter Description Banner */}
      <div
        className="mb-3 p-2 px-3 d-flex align-items-center"
        style={{
          backgroundColor: "#FFF5F5",

          borderRadius: "0.375rem",
          fontSize: "13px",
          color: "#4A5568",
        }}
      >
        <span className="fw-semibold me-2" style={{ color: "#E5484D" }}>
          {type === "unVerified"
            ? "Aadhaar Verified:"
            : type === "Unverified Faces"
              ? "Face Verification Failed:"
              : "OTP Verified:"}
        </span>
        <span>
          {type === "unVerified"
            ? "Displays users who have completed Aadhaar verification but didn't completed face verification."
            : type === "Unverified Faces"
              ? "Displays users whose face verification failed."
              : "Displays users who have verified their mobile number via OTP but didn't verified their Adhaar."}
        </span>
      </div>

      {loading ? (
        <Spinner />
      ) : (
        <div className="table-responsive">
          <table className="table table-striped align-middle shadow-sm">
            <thead className="table-light">
              <tr>
                <th>Sr No.</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone Number</th>
                <th style={{ minWidth: "180px" }}>Address</th>
                <th>T & C Accepted</th>
                <th>Created At</th>

                <th style={{ textAlign: "center" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {admins?.length > 0 ? (
                admins.map((admin, index) => (
                  <tr key={admin._id || index}>
                    <td>{currentPage * itemsPerPage + index + 1}</td>
                    <td>
                      {(admin?.isRead === false || admin?.isRead === 0) && (
                        <span
                          style={{
                            height: "8px",
                            width: "8px",
                            backgroundColor: "#E5484D",
                            borderRadius: "50%",
                            display: "inline-block",
                            marginRight: "6px",
                            verticalAlign: "middle",
                          }}
                          title="Unread"
                        />
                      )}
                      {admin.name
                        ? `${admin.name.charAt(0).toUpperCase()}${admin.name.slice(1)}`
                        : "_"}
                    </td>
                    <td>{admin.email ? admin.email : "_"}</td>
                    <td>
                      {admin.phoneNumber
                        ? `${admin.countryCode || ""} ${admin.phoneNumber}`
                        : "_"}
                    </td>
                    <td>
                      <ReadMoreText text={admin.address} maxLength={30} maxWidth="200px" />
                    </td>
                    <td>{admin.isTermsAndConditions ? "Accepted" : ""}</td>
                    <td>
                      {new Date(admin.createdAt).toLocaleDateString("en-CA", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                      {", "}
                      {new Date(admin.createdAt).toLocaleTimeString("en-CA", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <Dropdown placement="bottom-end">
                        <Dropdown.Toggle
                          variant="light"
                          id={`dropdown-basic-${index}`}
                          style={{ padding: "4px 8px" }}
                        >
                          {svg1}
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item
                            onClick={() => {
                              navigate(`/user-detail/${admin._id}`, {
                                state: {
                                  search,
                                  startDate,
                                  endDate,
                                  currentPage,
                                  activeTab,
                                  type,
                                  locationText,
                                  coordinates,
                                  isRead: admin.isRead,
                                },
                              });
                            }}
                          >
                            View
                          </Dropdown.Item>
                          {type === "Unverified Faces" && <Dropdown.Item onClick={() => triggerConfirm("accept", admin._id)}>
                            Approve
                          </Dropdown.Item>}
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="text-center py-4 text-muted">
                    No records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="card-footer clearfix mt-3">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-center text-center text-md-start">
          <div className="dataTables_info mb-3 mb-md-0" style={{ color: "#FFE4E6" }}></div>
          <div className="d-flex align-items-center">
            {totalPages > 0 && (
              <Pagination
                pageCount={totalPages}
                pageValue={currentPage}
                setPage={setCurrentPage}
              />
            )}
          </div>
        </div>
      </div>

      {/* Formik Detail Editor Management Modal */}
      <Modal size="xl" show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>{selectedAdmin ? "Edit User" : "Add User"}</Modal.Title>
        </Modal.Header>
        <Formik
          initialValues={
            selectedAdmin
              ? {
                userId: selectedAdmin._id,
                name: selectedAdmin.name || "",
                image: selectedAdmin.image || "",
              }
              : {
                name: "",
                image: "",
              }
          }
          enableReinitialize
          validationSchema={AdminSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, values, setFieldValue }) => (
            <FormikForm>
              <Modal.Body>
                <div className="mb-3">
                  <label className="form-label">Name</label>
                  <Field
                    name="name"
                    placeholder="ex. John Doe"
                    className={`form-control ${errors.name && touched.name ? "is-invalid" : ""}`}
                  />
                  <ErrorMessage name="name" component="div" className="text-danger" />
                </div>

                <div className="mb-3">
                  <label className="form-label">Image</label>
                  <input
                    type="file"
                    accept="image/*"
                    className="form-control"
                    ref={fileInputRef}
                    onChange={(e) => handleFileUpload(e, "image", setFieldValue)}
                  />
                  <ErrorMessage name="image" component="div" className="text-danger mt-1" />
                </div>

                {uploading ? (
                  <div className="text-success mt-2">Uploading image...</div>
                ) : imageLoading ? (
                  <div>loading...</div>
                ) : (
                  preview && (
                    <div className="mb-3 text-center mt-3">
                      <div style={{ display: "inline-block", position: "relative" }}>
                        <img
                          src={preview}
                          alt="preview"
                          style={{
                            width: "120px",
                            height: "120px",
                            objectFit: "cover",
                            borderRadius: "10px",
                            border: "1px solid #ddd",
                          }}
                        />
                        <button
                          type="button"
                          className="btn btn-danger btn-sm shadow"
                          style={{
                            position: "absolute",
                            top: "-8px",
                            right: "-8px",
                            borderRadius: "50%",
                            width: "24px",
                            height: "24px",
                            padding: "0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            lineHeight: "1",
                            fontSize: "16px",
                            fontWeight: "bold",
                          }}
                          onClick={() => {
                            setPreview("");
                            if (fileInputRef.current) fileInputRef.current.value = "";
                            setFieldValue("image", "");
                          }}
                        >
                          &times;
                        </button>
                      </div>
                    </div>
                  )
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={handleCloseModal}>
                  Close
                </Button>
                <Button
                  type="submit"
                  style={{ backgroundColor: "#E5484D", border: "none", color: "white" }}
                  disabled={uploading}
                >
                  {submit ? "Saving" : "Save Changes"}
                </Button>
              </Modal.Footer>
            </FormikForm>
          )}
        </Formik>
      </Modal>

      {/* Confirmation Action Handler Modal */}
      <Modal
        show={confirmState.show}
        onHide={() => setConfirmState({ ...confirmState, show: false })}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Action</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0 fs-5">{confirmState.message}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setConfirmState({ ...confirmState, show: false })}
          >
            Cancel
          </Button>
          <Button
            variant={
              confirmState.action === "delete" || confirmState.status === "Reject"
                ? "danger"
                : "primary"
            }
            style={
              confirmState.action !== "delete" && confirmState.status !== "Reject"
                ? {
                  backgroundColor: "#E5484D",
                  borderColor: "#FFE4E6",
                  color: "white",
                }
                : {}
            }
            onClick={confirmActionHandler}
          >
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 🌟 Export Modal */}
      <Modal
        show={showExportModal}
        onHide={() => setShowExportModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ color: "#E5484D" }}>Export Users Data</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleExportSubmit}>
          <Modal.Body>
            <div className="mb-3">
              <label className="form-label fw-bold">Export Format</label>
              <select
                className="form-select"
                style={{ borderRadius: "0.5rem", borderColor: "#E5484D" }}
                value={exportType}
                onChange={(e) => setExportType(e.target.value)}
              >
                <option value="excel">Excel (.xlsx)</option>
                <option value="csv">CSV (.csv)</option>
              </select>
            </div>

            <div className="mb-3">
              <label className="form-label fw-bold">Status</label>
              <select
                className="form-select"
                style={{ borderRadius: "0.5rem", borderColor: "#E5484D" }}
                value={exportStatus}
                onChange={(e) => setExportStatus(e.target.value)}
              >
                <option value="Pending">OTP Verified</option>
                <option value="Unverified Faces">Face Verification Failed</option>
                <option value="unVerified">Aadhaar Verified</option>
              </select>
            </div>

            <div className="mb-3">
              <label className="form-label fw-bold">
                Start Date <span className="text-danger">*</span>
              </label>
              <input
                type="date"
                className="form-control"
                style={{ borderRadius: "0.5rem", borderColor: "#E5484D" }}
                max={today}
                value={exportStartDate}
                onChange={(e) => {
                  setExportStartDate(e.target.value);
                  if (exportEndDate && e.target.value > exportEndDate) {
                    setExportEndDate("");
                  }
                }}
                required
              />
            </div>

            <div className="mb-3">
              <label className="form-label fw-bold">
                End Date <span className="text-danger">*</span>
              </label>
              <input
                type="date"
                className="form-control"
                style={{ borderRadius: "0.5rem", borderColor: "#E5484D" }}
                min={exportStartDate}
                max={today}
                value={exportEndDate}
                onChange={(e) => setExportEndDate(e.target.value)}
                disabled={!exportStartDate}
                required
              />
            </div>

            <div className="mb-3">
              <label className="form-label fw-bold">
                Location <span className="text-muted fw-normal">(Optional)</span>
              </label>
              <LocationSearchDropdown
                value={exportLocationText}
                onChange={setExportLocationText}
                onSelect={(locationData) => {
                  setExportLocationText(locationData.name);
                  const lat = locationData.location?.coordinates?.[1];
                  const long = locationData.location?.coordinates?.[0];
                  setExportCoordinates({ lat, long });
                }}
              />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowExportModal(false)}
              disabled={exportLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={exportLoading}
              style={{
                backgroundColor: exportType === "excel" ? "#21a366" : "#E5484D",
                borderColor: exportType === "excel" ? "#21a366" : "#E5484D",
                color: "white",
              }}
            >
              {exportLoading ? "Exporting..." : exportType === "excel" ? "Export Excel" : "Export CSV"}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
    </div>
  );
};

export default RequestPage;